import {
  actionChannel,
  call,
  CallEffect,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeLatest,
  ActionChannelEffect,
  take,
  TakeEffect,
  delay,
  flush,
  FlushEffect,
  fork
} from 'redux-saga/effects';

import {
  getProjectScripts,
  ProjectScript,
  getProjectScriptsSuccess,
  getProjectScriptsError,
  updateEditingScript,
  syncSuccess,
  deleteScript,
  syncError
} from 'js/reducers/projectScripts';
import {
  createProjectScript,
  deleteProjectScript,
  getProjectScriptsByProjectId,
  updateProjectScript
} from 'js/shared/helpers/app-services/AppServices/projectScripts';
import { showError } from 'js/actionCreators/uiActions';

function* getProjectScriptsSaga({
  payload
}: ReturnType<typeof getProjectScripts>): Generator<CallEffect | PutEffect, void, ProjectScript[]> {
  try {
    const scripts = yield call(getProjectScriptsByProjectId, payload.projectId);
    yield put(
      getProjectScriptsSuccess({
        projectId: payload.projectId,
        scripts
      })
    );
  } catch (error) {
    console.error('Error fetching project scripts:', error);
    yield put(
      getProjectScriptsError({
        projectId: payload.projectId
      })
    );
    yield put(
      showError({
        message: 'Error fetching project scripts. Please try again later.',
        description: error instanceof Error ? error.message : String(error)
      })
    );
  }
}

function* syncScriptToServerSaga(): Generator<
  SelectEffect | PutEffect | ActionChannelEffect | TakeEffect | CallEffect | FlushEffect<ActionChannelEffect>,
  void
> {
  // Buffer all incoming actions
  const editChannel = yield actionChannel(updateEditingScript.type);

  while (true) {
    // Wait for an action to be dispatched
    yield take(editChannel);

    // debounce the sync to server by waiting for a short period of time before proceeding
    yield delay(500);

    // Clear any additional actions that have been dispatched during the delay
    yield flush(editChannel);

    // Get the latest state of the editing script from the store
    const edits = yield select(state => state.projectScripts.editingScript);

    if (!edits) {
      continue; // No script to sync, continue to the next iteration
    }

    try {
      if (!edits.id) {
        const response = yield call(createProjectScript, edits);
        yield put(syncSuccess({ script: response }));
      } else {
        const response = yield call(updateProjectScript, edits);
        yield put(syncSuccess({ script: response }));
      }
    } catch (error) {
      console.error('Error syncing script to server:', error);
      yield put(syncError());
      yield put(
        showError({
          message: 'Error syncing script to server. Please try again later.',
          description: error instanceof Error ? error.message : String(error)
        })
      );
    }
  }
}

function* deleteScriptSaga({
  payload
}: ReturnType<typeof deleteScript>): Generator<CallEffect | PutEffect, void, void> {
  try {
    yield call(deleteProjectScript, payload.scriptId);
  } catch (error) {
    console.error('Error deleting project script:', error);
    yield put(
      showError({
        message: 'Error deleting project script. Please try again later.',
        description: error instanceof Error ? error.message : String(error)
      })
    );
  }
}

export default function* projectScriptsSagas() {
  yield takeLatest(getProjectScripts.type, getProjectScriptsSaga);
  yield fork(syncScriptToServerSaga);
  yield takeLatest(deleteScript.type, deleteScriptSaga);
}
